/**
 * OperatorShell — the Direction A "CRM Operator" page chrome.
 *
 * Track 9 redesign: dual navigation pattern.
 *   - Fixed left sidebar: 200px expanded / 56px collapsed (OperatorSidebar)
 *   - 64px white sticky top bar: breadcrumbs + search + primary action (OperatorTopbar)
 *   - 40px context-tab strip below topbar: Accounts | Sales Path | Question Bank |
 *     Methodology | Manuals (ContextTabs) — horizontal context-switchers
 *   - White main canvas with 24px outer padding
 *
 * Mobile (<lg): sidebar collapses to a drawer; menu icon sits top-left.
 * Topbar and ContextTabs stay sticky.
 *
 * The main content wrapper reads the sidebar collapsed state from localStorage
 * so it shifts correctly without a React re-render cascade.
 *
 * REUSABLE: any future page can drop into this shell — call with breadcrumbs
 * + content and you inherit the full CRM Operator look.
 */
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import OperatorSidebar from "./OperatorSidebar";
import OperatorTopbar, { type OperatorTopbarProps } from "./OperatorTopbar";
import OperatorPageHeader from "./OperatorPageHeader";
import ContextTabs from "./ContextTabs";
import { tokens } from "./tokens";
import { ImpersonationBanner } from "@/components/ImpersonationBanner"; // W66.X15

const SIDEBAR_STORAGE_KEY = "aip:sidebar-collapsed";

export interface OperatorShellProps {
  breadcrumbs: OperatorTopbarProps["breadcrumbs"];
  searchPlaceholder?: string;
  primaryAction?: OperatorTopbarProps["primaryAction"];

  // Optional page header. Omit for routes that render their own header.
  pageTitle?: string;
  pageMeta?: string;
  pageActions?: React.ReactNode;

  /** Set to false to hide the ContextTabs strip on pages that manage their own sub-nav. */
  showContextTabs?: boolean;

  children: React.ReactNode;
}

export default function OperatorShell({
  breadcrumbs,
  searchPlaceholder,
  primaryAction,
  pageTitle,
  pageMeta,
  pageActions,
  showContextTabs = true,
  children,
}: OperatorShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Mirror sidebar collapsed state so the main content shifts correctly.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored !== null) return stored === "true";
    } catch { /* ignore */ }
    return typeof window !== "undefined" && window.innerWidth < 1280;
  });

  // Listen for storage changes (sidebar toggle writes to localStorage)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === SIDEBAR_STORAGE_KEY) {
        setSidebarCollapsed(e.newValue === "true");
      }
    }
    window.addEventListener("storage", onStorage);

    // Poll at short intervals to pick up same-tab localStorage changes
    // (storage events only fire across tabs, not within the same tab).
    const interval = setInterval(() => {
      try {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (stored !== null) {
          const val = stored === "true";
          setSidebarCollapsed((prev) => (prev !== val ? val : prev));
        }
      } catch { /* ignore */ }
    }, 100);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, []);

  const marginLeft = sidebarCollapsed
    ? tokens.space.sidebarCollapsedWidth
    : tokens.space.sidebarWidth;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: tokens.color.canvas,
        fontFamily: tokens.font.body,
        color: tokens.color.textPrimary,
      }}
    >
      {/* W66.X15: impersonation banner at top of every OperatorShell page */}
      <ImpersonationBanner />

      {/* Desktop sidebar (lg+) */}
      <div className="hidden lg:block">
        <OperatorSidebar />
      </div>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div className="lg:hidden">
          {/* Backdrop — tap outside sidebar to close */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
              zIndex: 60,
            }}
          />
          <div style={{ zIndex: 70, position: "relative" }}>
            <OperatorSidebar onNavigate={() => setDrawerOpen(false)} />
          </div>
          {/* Close-X: lives inside the dark sidebar panel at its top-right.
              Transparent background + white-stroke icon ensures visibility
              against the dark sidebar (tokens.color.black). Positioned at
              top-right of the sidebar width so it never occludes nav items. */}
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed",
              top: 12,
              left: "calc(200px - 44px)",
              width: 36,
              height: 36,
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              zIndex: 80,
              color: "#FFFFFF",
            }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Main content wrapper — shifts with sidebar on desktop */}
      {/* W27: removed duplicate minHeight:100vh — outer root div already enforces it.
          This inner wrapper is a block-level child; it does NOT need its own 100vh
          minimum. Having two stacked 100vh containers caused the full-page
          screenshot to capture 200vh, making every page appear doubled. */}
      <div
        className="lg:transition-[margin-left]"
        style={{
          display: "flex",
          flexDirection: "column",
          background: tokens.color.canvas,
          overflowX: "hidden",   // prevent children from escaping the sidebar inset
        }}
      >
        {/* Inline style on a wrapping div for the lg+ margin so it responds to
            collapse state. On mobile (< lg) no left margin — sidebar is a drawer.
            W66.X27 (2026-06-11): added minWidth:0 + width:auto so the wrapper
            can shrink inside its flex parent on tablet widths, preventing child
            content (e.g. Manuals header) from forcing horizontal overflow. */}
        <div
          className="hidden lg:block"
          style={{ marginLeft, minWidth: 0, width: "auto" }}
        >
          {/* Topbar */}
          <OperatorTopbar
            breadcrumbs={breadcrumbs}
            searchPlaceholder={searchPlaceholder}
            primaryAction={primaryAction}
          />

          {/* Context-tab strip */}
          {showContextTabs && <ContextTabs />}

          <main
            style={{
              padding: tokens.space.pagePad,
              flex: 1,
              background: tokens.color.canvas,
            }}
          >
            {pageTitle && (
              <OperatorPageHeader title={pageTitle} meta={pageMeta} actions={pageActions} />
            )}
            {children}
          </main>
        </div>

        {/* Mobile layout — no sidebar margin */}
        {/* W36.1 FIX: removed display:"flex" from inline style — it overrode the lg:hidden
            Tailwind class on desktop, causing children (page content) to render twice:
            once in the desktop div and again here. flexDirection + flex moved to className. */}
        <div className="lg:hidden flex flex-col flex-1">
          {/* Mobile-only hamburger — fixed top-left, visible only when drawer is closed */}
          {!drawerOpen && (
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setDrawerOpen(true)}
              style={{
                position: "fixed",
                top: 14, left: 12,
                width: 36, height: 36, borderRadius: 6,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: "#FFFFFF", border: `1px solid ${tokens.color.border}`, cursor: "pointer",
                zIndex: 80,
              }}
            >
              <Menu size={16} />
            </button>
          )}

          <OperatorTopbar
            breadcrumbs={breadcrumbs}
            searchPlaceholder={searchPlaceholder}
            primaryAction={primaryAction}
          />

          {showContextTabs && <ContextTabs />}

          <main
            style={{
              padding: tokens.space.pagePad,
              flex: 1,
              background: tokens.color.canvas,
            }}
          >
            {pageTitle && (
              <OperatorPageHeader title={pageTitle} meta={pageMeta} actions={pageActions} />
            )}
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
